import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload } from "lucide-react";
import { api, imgUrl, formatApiError } from "../../api";
import { useSettings } from "../../context/SettingsContext";

function ImageField({ label, value, onChange, testid }) {
  const ref = useRef(null);
  const [uploading, setUploading] = useState(false);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/api/admin/upload", fd);
      onChange(data.url);
      toast.success("Uploaded");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      if (ref.current) ref.current.value = "";
    }
  };

  return (
    <div className="form-block">
      <span className="eyebrow">{label}</span>
      <div className="image-field">
        {value ? (
          <div className="image-preview">
            <img src={imgUrl(value)} alt={label} />
          </div>
        ) : (
          <div className="image-preview empty">No image</div>
        )}
        <div className="image-field-actions">
          <label className="button outline-button">
            <input ref={ref} type="file" accept="image/*" onChange={upload} hidden data-testid={`${testid}-upload`} />
            <Upload size={14} /> {uploading ? "Uploading…" : "Upload image"}
          </label>
          {value && (
            <button
              type="button"
              className="button outline-button"
              onClick={() => onChange(null)}
              data-testid={`${testid}-remove`}
            >
              Remove
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function AdminSettings() {
  const { settings: current, refresh } = useSettings();
  const [form, setForm] = useState(current);
  const [saving, setSaving] = useState(false);
  const [pwd, setPwd] = useState({ current_password: "", new_password: "" });

  useEffect(() => {
    setForm(current);
  }, [current]);

  const bind = (key) => ({
    value: form[key] ?? "",
    onChange: (e) => setForm({ ...form, [key]: e.target.value }),
  });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/api/admin/settings", form);
      toast.success("Settings saved");
      refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    try {
      await api.post("/api/auth/change-password", pwd);
      toast.success("Admin password updated");
      setPwd({ current_password: "", new_password: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="admin-page">
      <div className="admin-page-head">
        <div>
          <span className="eyebrow">Configuration</span>
          <h1>Settings.</h1>
        </div>
      </div>
      <form onSubmit={save} className="admin-form">
        <section className="admin-form-section">
          <h2>Brand</h2>
          <div className="form-grid">
            <label>
              Brand name
              <input {...bind("brand_name")} data-testid="settings-brand-name" />
            </label>
            <label>
              WhatsApp number (with country code)
              <input {...bind("whatsapp_number")} data-testid="settings-whatsapp" />
            </label>
            <label>
              Email
              <input type="email" {...bind("email")} data-testid="settings-email" />
            </label>
            <label>
              Instagram handle
              <input {...bind("instagram_handle")} data-testid="settings-instagram" />
            </label>
            <label className="span-2">
              Footer note
              <input {...bind("footer_note")} data-testid="settings-footer-note" />
            </label>
          </div>
          <ImageField
            label="Logo"
            value={form.logo_url}
            onChange={(v) => setForm({ ...form, logo_url: v })}
            testid="settings-logo"
          />
        </section>

        <section className="admin-form-section">
          <h2>Announcement bar</h2>
          <div className="form-grid">
            <label className="toggle span-2">
              <input
                type="checkbox"
                checked={form.announcement_active}
                onChange={(e) => setForm({ ...form, announcement_active: e.target.checked })}
                data-testid="settings-announcement-active"
              />
              Show announcement bar
            </label>
            <label className="span-2">
              Announcement text
              <input {...bind("announcement_bar")} data-testid="settings-announcement-text" />
            </label>
            <label className="toggle span-2">
              <input
                type="checkbox"
                checked={form.launch_offer_active}
                onChange={(e) => setForm({ ...form, launch_offer_active: e.target.checked })}
                data-testid="settings-launch-offer-active"
              />
              Show launch offer badge
            </label>
            <label className="span-2">
              Launch offer text
              <input {...bind("launch_offer_text")} data-testid="settings-launch-offer-text" />
            </label>
          </div>
        </section>

        <section className="admin-form-section">
          <h2>Homepage — Hero</h2>
          <div className="form-grid">
            <label>
              Hero eyebrow
              <input {...bind("hero_eyebrow")} data-testid="settings-hero-eyebrow" />
            </label>
            <label>
              Line 1
              <input {...bind("hero_title_line1")} data-testid="settings-hero-line1" />
            </label>
            <label>
              Line 2 (accent)
              <input {...bind("hero_title_line2")} data-testid="settings-hero-line2" />
            </label>
            <label className="span-2">
              Subtitle
              <textarea rows={2} {...bind("hero_subtitle")} data-testid="settings-hero-subtitle" />
            </label>
          </div>
          <ImageField
            label="Hero image"
            value={form.hero_image}
            onChange={(v) => setForm({ ...form, hero_image: v })}
            testid="settings-hero-image"
          />
        </section>

        <section className="admin-form-section">
          <h2>About block</h2>
          <div className="form-grid">
            <label className="span-2">
              Heading
              <input {...bind("about_heading")} data-testid="settings-about-heading" />
            </label>
            <label className="span-2">
              Body
              <textarea rows={3} {...bind("about_body")} data-testid="settings-about-body" />
            </label>
          </div>
        </section>

        <section className="admin-form-section">
          <h2>Editorial banner</h2>
          <div className="form-grid">
            <label>
              Eyebrow
              <input {...bind("editorial_banner_eyebrow")} data-testid="settings-editorial-eyebrow" />
            </label>
            <label>
              Line 1
              <input {...bind("editorial_banner_title_line1")} data-testid="settings-editorial-line1" />
            </label>
            <label>
              Line 2 (accent)
              <input {...bind("editorial_banner_title_line2")} data-testid="settings-editorial-line2" />
            </label>
          </div>
          <ImageField
            label="Banner image"
            value={form.editorial_banner_image}
            onChange={(v) => setForm({ ...form, editorial_banner_image: v })}
            testid="settings-banner-image"
          />
        </section>

        <div className="admin-form-actions">
          <button className="button dark-button" type="submit" disabled={saving} data-testid="settings-save-button">
            {saving ? "Saving…" : "Save all settings"}
          </button>
        </div>
      </form>

      <section className="admin-form-section" style={{ marginTop: 60 }}>
        <h2>Admin password</h2>
        <form onSubmit={changePassword} className="form-grid" style={{ maxWidth: 520 }}>
          <label className="span-2">
            Current password
            <input
              required
              type="password"
              value={pwd.current_password}
              onChange={(e) => setPwd({ ...pwd, current_password: e.target.value })}
              data-testid="admin-current-password"
            />
          </label>
          <label className="span-2">
            New password
            <input
              required
              type="password"
              minLength={6}
              value={pwd.new_password}
              onChange={(e) => setPwd({ ...pwd, new_password: e.target.value })}
              data-testid="admin-new-password"
            />
          </label>
          <button className="button outline-button span-2" type="submit" data-testid="admin-change-password-button">
            Update password
          </button>
        </form>
      </section>
    </div>
  );
}
